import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Check, Clock3, MapPin, Minus, Plus, ShieldCheck, ShoppingBag, Users,
} from "lucide-react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO from "../SEO/SEO";
import { get, post } from "../../utils/request";
import { notifyCartUpdated } from "../Cart/CartContext";
import "./airport-lounges.scss";

const localDate = (offsetDays = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const travellerId = (traveller) => String(traveller?.id || traveller?._id || "");
const loungeImage = (lounge) => lounge?.mediaAssets?.[0]?.url
  || lounge?.providerImageUrl
  || "/images/editorial/mountain-hero.webp";

export default function AirportLoungeDetailPage() {
  const { loungeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const accountUser = useSelector((state) => state.userData.user?.user);
  const [lounge, setLounge] = useState(null);
  const [travellers, setTravellers] = useState([]);
  const [selectedTravellerIds, setSelectedTravellerIds] = useState([]);
  const [visitDate, setVisitDate] = useState(localDate(1));
  const [guests, setGuests] = useState(1);
  const [loading, setLoading] = useState(true);
  const [travellersLoading, setTravellersLoading] = useState(Boolean(accountUser));
  const [submitting, setSubmitting] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    get(`/lounge/detail/${loungeId}`)
      .then((result) => active && setLounge(result))
      .catch((requestError) => active && setError(requestError?.message || "This lounge is not currently available."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [loungeId]);

  useEffect(() => {
    if (!accountUser) {
      setTravellers([]);
      setTravellersLoading(false);
      return undefined;
    }
    let active = true;
    setTravellersLoading(true);
    get("/passengers/getAll")
      .then((result) => active && setTravellers(Array.isArray(result) ? result : []))
      .catch(() => active && setTravellers([]))
      .finally(() => active && setTravellersLoading(false));
    return () => { active = false; };
  }, [accountUser]);

  useEffect(() => {
    setSelectedTravellerIds((current) => current.slice(0, guests));
  }, [guests]);

  const selectedTravellers = useMemo(() => {
    const selected = new Set(selectedTravellerIds);
    return travellers.filter((traveller) => selected.has(travellerId(traveller)));
  }, [selectedTravellerIds, travellers]);

  const toggleTraveller = (traveller) => {
    const id = travellerId(traveller);
    setSelectedTravellerIds((current) => {
      if (current.includes(id)) return current.filter((value) => value !== id);
      if (current.length >= guests) return current;
      return [...current, id];
    });
  };

  const createCartItem = async (destination) => {
    if (!accountUser) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    if (!visitDate) return toast.error("Select your lounge visit date.");
    if (selectedTravellers.length !== guests) {
      return toast.error(`Select exactly ${guests} traveller${guests === 1 ? "" : "s"}.`);
    }

    setSubmitting(destination);
    try {
      const preorder = await post("/lounge/preorder", {
        loungeId,
        visitDate,
        guests,
        participantInfos: selectedTravellers.map((traveller) => ({
          ...traveller,
          passengerId: travellerId(traveller),
        })),
      });
      const preorderId = preorder?.preorderInfo?.preorderId;
      if (!preorderId) throw new Error("The lounge selection could not be reserved.");
      const cartResult = await post("/cart/addItem", {
        serviceType: "airportLounge",
        item: { preorderId },
        source: "airport_lounge_detail",
      });
      notifyCartUpdated();
      if (destination === "checkout") {
        navigate("/cart/checkout", { state: { cartItemIds: [cartResult.cartItemId] } });
      } else {
        toast.success("Airport lounge added to your cart.");
        navigate("/cart");
      }
    } catch (requestError) {
      toast.error(requestError?.message || "Unable to reserve this lounge.");
    } finally {
      setSubmitting("");
    }
  };

  if (loading) return <><Navbar /><main className="lounge-detail-state">Loading live lounge details…</main><Footer /></>;
  if (error || !lounge) return <><Navbar /><main className="lounge-detail-state"><h1>Lounge unavailable</h1><p>{error}</p><Link to="/airport-lounges">Search airport lounges</Link></main><Footer /></>;

  const hours = Object.entries(lounge.operatingHours || {}).filter(([, value]) => String(value || "").trim());
  const images = (lounge.mediaAssets || []).filter((asset) => asset?.url);

  return (
    <><SEO title={`${lounge.loungeName} | Airport Lounge`} canonicalPath={`/airport-lounges/${loungeId}`} /><Navbar />
      <main className="lounge-detail-page">
        <header className="lounge-detail-hero">
          <img src={loungeImage(lounge)} alt="" />
          <div className="lounge-detail-hero__overlay" />
          <div className="lounge-detail-hero__content">
            <Link to={`/airport-lounges/search?country=${encodeURIComponent(lounge.countryCode || "")}&countryName=${encodeURIComponent(lounge.country || "")}`}>← Back to results</Link>
            <p>{lounge.airportIata || lounge.city} · Airport Lounge</p>
            <h1>{lounge.loungeName}</h1>
            <span><MapPin /> {[lounge.airport, lounge.terminal, lounge.hall].filter(Boolean).join(" · ")}</span>
          </div>
        </header>

        <div className="lounge-detail-layout">
          <div className="lounge-detail-content">
            {images.length > 1 && <div className="lounge-detail-gallery">{images.slice(0, 4).map((asset) => <img key={asset._id || asset.url} src={asset.url} alt="" loading="lazy" />)}</div>}

            <section className="lounge-detail-card">
              <p className="teleport-eyebrow">Lounge information</p>
              <h2>Relax before your flight</h2>
              <p className="lounge-detail-copy">{lounge.loungeInformation || "Enjoy a more comfortable airport experience before departure."}</p>
              <div className="lounge-detail-facts">
                <span><Clock3 /> {lounge.durationMinutes ? `Up to ${lounge.durationMinutes} minutes` : "Visit duration shown at booking"}</span>
                <span><Users /> {lounge.providerAudience === "ALL" ? "Open to all travellers" : lounge.providerAudience}</span>
                <span><MapPin /> {lounge.city}, {lounge.country}</span>
              </div>
            </section>

            {lounge.nearby && <section className="lounge-detail-card"><p className="teleport-eyebrow">Finding the lounge</p><h2>Nearby and directions</h2><p className="lounge-detail-copy">{lounge.nearby}</p></section>}

            {lounge.availableServices?.length > 0 && <section className="lounge-detail-card"><p className="teleport-eyebrow">Facilities</p><h2>Available services</h2><div className="lounge-service-list">{lounge.availableServices.map((service) => <span key={service}><Check /> {service}</span>)}</div></section>}

            {hours.length > 0 && <section className="lounge-detail-card"><p className="teleport-eyebrow">Opening hours</p><h2>Plan your visit</h2><div className="lounge-hours">{hours.map(([day, value]) => <div key={day}><span>{day}</span><strong>{value}</strong></div>)}</div></section>}
          </div>

          <aside className="lounge-booking-card">
            <p className="teleport-eyebrow">Reserve lounge access</p>
            <h2>{lounge.pricing?.displayFromPriceLabel || "Live price at checkout"}</h2>
            <label>Visit date<input type="date" min={localDate()} value={visitDate} onChange={(event) => setVisitDate(event.target.value)} /></label>
            <div className="lounge-guest-stepper"><span><strong>Travellers</strong><small>Select saved traveller information below</small></span><div><button type="button" onClick={() => setGuests((value) => Math.max(1, value - 1))} disabled={guests <= 1}><Minus /></button><b>{guests}</b><button type="button" onClick={() => setGuests((value) => Math.min(6, value + 1))} disabled={guests >= 6}><Plus /></button></div></div>

            <div className="lounge-travellers">
              <strong>Traveller information</strong>
              {!accountUser ? <p>Sign in to choose saved travellers and reserve access.</p> : travellersLoading ? <p>Loading saved travellers…</p> : travellers.length === 0 ? <p>No saved travellers yet. <Link to="/my-profile" state={{ activeTab: "travellers" }}>Add traveller information</Link>.</p> : travellers.map((traveller) => {
                const id = travellerId(traveller);
                const selected = selectedTravellerIds.includes(id);
                return <button type="button" key={id} className={selected ? "is-selected" : ""} onClick={() => toggleTraveller(traveller)} disabled={!selected && selectedTravellerIds.length >= guests}><span>{String(traveller.firstName || "T").charAt(0)}</span><span><b>{traveller.firstName} {traveller.lastName}</b><small>{traveller.type || "Traveller"} · {traveller.documentType || "Passport"}</small></span>{selected && <Check />}</button>;
              })}
            </div>

            <div className="lounge-booking-actions">
              <button type="button" className="lounge-add-cart" onClick={() => createCartItem("cart")} disabled={Boolean(submitting)}><ShoppingBag /> {submitting === "cart" ? "Adding…" : "Add to cart"}</button>
              <button type="button" className="lounge-checkout" onClick={() => createCartItem("checkout")} disabled={Boolean(submitting)}>{submitting === "checkout" ? "Preparing checkout…" : "Checkout now"}</button>
            </div>
            <p className="lounge-booking-assurance"><ShieldCheck /> Live availability and price are confirmed before payment.</p>
          </aside>
        </div>
      </main><Footer /></>
  );
}
